// Creator-business (크리에이터 단가) API client for the /platform table.
// Ported from leferi-monorepo (apps/admin) — same lecommend admin BFF, but here
// on the portal's plain-fetch stack (no axios / React Query). Auth is a Bearer
// token (like the source's httpClient), read from localStorage.
//
// Calls the backend directly at localhost:8085. NOTE: this is cross-origin from
// the Vite dev server (localhost:5173), so the backend MUST send CORS headers
// (Access-Control-Allow-Origin for the dev origin) or the browser blocks it.
// Vite dev 프록시(/lecommend-api → localhost:8085/api)로 same-origin(5173) 호출 → CORS 회피.
// 원격 dev 백엔드로 붙이려면 vite 실행 시 LECOMMEND_API_TARGET 지정.
const BASE_URL = '/creator-business'
const TOKEN_KEYS = ['lecommend_token', 'token'] as const

export function getLecommendToken(): string | null {
  for (const k of TOKEN_KEYS) {
    const v = typeof window !== 'undefined' ? window.localStorage.getItem(k) : null
    if (v) return v
  }
  return null
}
export function setLecommendToken(token: string): void {
  window.localStorage.setItem('lecommend_token', token.trim())
}

export class ApiError extends Error {
  status: number
  // true = 전송/HTTP 실패(401·404·5xx·non-JSON — status 코드가 유의미).
  // false = envelope 실패(HTTP 200 + success:false 비즈니스 에러 — status 코드 무의미).
  transport: boolean
  constructor(message: string, status: number, transport = true) {
    super(message)
    this.status = status
    this.transport = transport
  }
}

// ── Types (mirror the CreatorsView.Item DTO — 31 preformatted string fields) ──
export interface CreatorBusinessItem {
  salesforceCreatorId: string
  name: string
  creatorImageUrl: string
  creatorLabel: string
  contractType: string
  manager: string
  language: string
  personalColor: string
  skinType: string
  skinWorry: string
  preferenceIngredient: string
  dislikeIngredient: string
  channelScale: string
  businessType: string
  businessCategory: string
  contentsGenre: string
  salesforceUpdatedAt: string
  youtubeSubscriberCount: string
  youtubeLink: string
  instagramFollowerCount: string
  instagramLink: string
  youtubePlanningVideoPrice: string
  youtubePplPrice: string
  youtubeShortsVideoPrice: string
  instagramImage4Price: string
  instagramVideo3060Price: string
  instagramRegramPrice: string
  licenseMarketing1mPrice: string
  licenseEcommerce1mPrice: string
  licenseOffline1mPrice: string
  liveCommerceGuestPrice: string
}

export interface PriceItemDef {
  key: keyof CreatorBusinessItem
  label: string
}
export interface PriceGroupDef {
  group: string
  items: PriceItemDef[]
}

// 단가(협업 상품) 컬럼 정의 — 테이블 그룹 헤더 + 계산기에서 공통 사용.
export const PRICE_GROUPS: PriceGroupDef[] = [
  {
    group: 'YouTube 단가',
    items: [
      { key: 'youtubePlanningVideoPrice', label: '기획 영상' },
      { key: 'youtubePplPrice', label: '일반 PPL' },
      { key: 'youtubeShortsVideoPrice', label: '쇼츠 영상' },
    ],
  },
  {
    group: 'Instagram 단가',
    items: [
      { key: 'instagramImage4Price', label: '이미지 (4장)' },
      { key: 'instagramVideo3060Price', label: '영상 (30~60초)' },
      { key: 'instagramRegramPrice', label: '리그램 1회' },
    ],
  },
  {
    group: 'License 단가 (1개월)',
    items: [
      { key: 'licenseMarketing1mPrice', label: '마케팅 활용' },
      { key: 'licenseEcommerce1mPrice', label: '이커머스 활용' },
      { key: 'licenseOffline1mPrice', label: '오프라인 활용' },
    ],
  },
  {
    group: '라이브 커머스',
    items: [{ key: 'liveCommerceGuestPrice', label: '게스트 출연' }],
  },
]
export const PRICE_ITEMS: PriceItemDef[] = PRICE_GROUPS.flatMap((g) => g.items)

export const DEFAULT_ORDER_BY = 'youtube.subscriber.desc'

// GET /creators query params — every filter is a CSV of ids ('' = no filter).
export interface GetCreatorBusinessParams {
  salesforceCreatorIds?: string
  creatorLabels?: string
  personalColors?: string
  skinTypes?: string
  skinWorries?: string
  preferenceIngredients?: string
  dislikeIngredients?: string
  channelScales?: string
  businessTypes?: string
  contentsGenres?: string
  languages?: string
  contractTypes?: string
  orderBy?: string
  [key: string]: string | undefined
}

export interface CreatorBusinessListResult {
  items: CreatorBusinessItem[]
  total: number
}

interface Envelope<T> {
  success: boolean
  data: T | null
  errorCode?: string
  errorMessage?: string
}

async function apiGet<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const qs = new URLSearchParams()
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v)
    }
  }
  const token = getLecommendToken()
  const res = await fetch(`${path}${qs.toString() ? `?${qs}` : ''}`, {
    headers: {
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  let body: Envelope<T> | null = null
  try {
    body = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON error */
  }
  if (res.status === 401) {
    throw new ApiError(body?.errorMessage ?? '인증이 필요합니다. (lecommend 토큰 필요)', 401)
  }
  if (!res.ok || !body?.success) {
    throw new ApiError(body?.errorMessage ?? `요청 실패 (${res.status})`, res.status)
  }
  return body.data as T
}

/** 크리에이터 단가 목록. API는 전체를 한 번에 반환 → 클라이언트에서 잘라 노출. */
export async function getCreatorBusinessCreators(
  params: GetCreatorBusinessParams,
): Promise<CreatorBusinessListResult> {
  const d = await apiGet<{ items?: CreatorBusinessItem[]; totalCount?: number }>(
    `${BASE_URL}/creators`,
    params,
  )
  const items = d?.items ?? []
  return { items, total: d?.totalCount ?? items.length }
}

/** 크리에이터 리스트 엑셀 추출 — 현재 필터/정렬 params 를 그대로 query 로 전달. */
export async function getCreatorBusinessCreatorsExcel(
  params: GetCreatorBusinessParams,
): Promise<void> {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) qs.append(k, v)
  }
  const token = getLecommendToken()
  const res = await fetch(`${BASE_URL}/creators/excel${qs.toString() ? `?${qs}` : ''}`, {
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
  if (res.status === 401) throw new ApiError('인증이 필요합니다. (lecommend 토큰 필요)', 401)
  if (!res.ok) throw new ApiError(`데이터 추출 실패 (${res.status})`, res.status)
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = cd.match(/filename="?([^"]+)"?/)
  const fileName = m?.[1] ? decodeURIComponent(m[1]) : 'creators.xlsx'
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

export interface FilterOption {
  id: string
  name: string
}
export interface CreatorBusinessFilterOptions {
  orderBys: FilterOption[]
  [key: string]: FilterOption[]
}

/** 필터 옵션 + 정렬 옵션 (FilterBar 용). */
export async function getCreatorBusinessFilterOptions(): Promise<CreatorBusinessFilterOptions> {
  const raw = await apiGet<Record<string, unknown>>(`${BASE_URL}/filter-options`)
  const toOptions = (v: unknown): FilterOption[] =>
    (Array.isArray(v) ? v : []).map((o) => {
      if (o && typeof o === 'object') {
        const r = o as Record<string, unknown>
        const id = String(r.id ?? r.value ?? r.code ?? r.key ?? r.name ?? '')
        const name = String(r.name ?? r.label ?? r.value ?? r.id ?? '')
        return { id, name }
      }
      return { id: String(o ?? ''), name: String(o ?? '') }
    })
  const dims = [
    'creators', 'labels', 'personalColors', 'skinTypes', 'skinWorries', 'preferenceIngredients',
    'dislikeIngredients', 'channelScales', 'businessTypes', 'contentsGenres', 'languages',
    'contractTypes', 'orderBys',
  ] as const
  const out = {} as CreatorBusinessFilterOptions
  for (const k of dims) out[k] = toOptions(raw[k])
  // 언어 응답 키가 다를 수 있어 폴백
  if (out.languages.length === 0)
    out.languages = toOptions(raw.language ?? raw.availableLanguages ?? raw.spokenLanguages)
  return out
}

// ── 상세 (GET /creators/:id) ──
export interface CreatorBusinessDetail extends CreatorBusinessItem {
  youtubeChannelName?: string
  instagramChannelName?: string
  salesforceUrl?: string
  salesforceCreatorUrl?: string
}
export async function getCreatorBusinessDetail(
  salesforceCreatorId: string,
): Promise<CreatorBusinessDetail> {
  return apiGet<CreatorBusinessDetail>(
    `${BASE_URL}/creators/${encodeURIComponent(salesforceCreatorId)}`,
  )
}

// ── 견적 계산기 카트 (POST /cart, POST /cart/excel) ──
export interface CartRequestProductItem { key: string }
export interface CartRequestCreator {
  salesforceCreatorId: string
  productItems: CartRequestProductItem[]
}
export interface CartRequestBody { creators: CartRequestCreator[] }

export interface CartResponseProductItem { key: string; name: string; price: string }
export interface CartSelectableProductItem { key: string; name: string }
export interface CartResponseCreator {
  salesforceId: string
  name: string
  imageUrl: string
  subtotal: string
  productItems: CartResponseProductItem[]
  selectableProductItems: CartSelectableProductItem[]
}
export interface CartSelectableCreator { salesforceId: string; name: string }
export interface CartData {
  creators: CartResponseCreator[]
  creatorCount: number
  productCount: number
  total: string
  selectableCreators: CartSelectableCreator[]
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const token = getLecommendToken()
  const res = await fetch(path, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  })
  let json: Envelope<T> | null = null
  try {
    json = (await res.json()) as Envelope<T>
  } catch {
    /* non-JSON */
  }
  if (res.status === 401) throw new ApiError('인증이 필요합니다. (lecommend 토큰 필요)', 401)
  if (!res.ok || !json?.success) {
    throw new ApiError(json?.errorMessage ?? `요청 실패 (${res.status})`, res.status)
  }
  return json.data as T
}

/** 카트 담기/조회. 빈 body 를 보내면 초기화. 금액(소계/단가/합계)은 서버가 계산. */
export async function postCreatorCart(body: CartRequestBody): Promise<CartData> {
  return apiPost<CartData>(`${BASE_URL}/cart`, body)
}

/** 카트 엑셀 추출 — 동일 body 로 xlsx 다운로드. */
export async function postCreatorCartExcel(body: CartRequestBody): Promise<void> {
  const token = getLecommendToken()
  const res = await fetch(`${BASE_URL}/cart/excel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body ?? {}),
  })
  if (!res.ok) throw new ApiError(`엑셀 추출 실패 (${res.status})`, res.status)
  const blob = await res.blob()
  const cd = res.headers.get('content-disposition') || ''
  const m = cd.match(/filename="?([^"]+)"?/)
  const fileName = m?.[1] ? decodeURIComponent(m[1]) : 'creator-estimate.xlsx'
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}
